import { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { api } from '../../api/client';
import { Search, X, Hash, Lock, ClipboardList, Layers, Users } from 'lucide-react';
import { getAvatarColor } from '../../utils/avatar';

interface GlobalSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialScope?: { id: string; type: string; name: string };
}

const isElectron = !!(window as any).electronAPI;

export default function GlobalSearchModal({ isOpen, onClose, initialScope }: GlobalSearchModalProps) {
    const { setActiveView, users, channels, directMessages, topics, tickets, _decryptMessages } = useApp();
    const [query, setQuery] = useState('');
    const [scope, setScope] = useState<{ id: string; type: string; name: string } | null>(initialScope || null);
    const [results, setResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [browserDecryptedCache, setBrowserDecryptedCache] = useState<any[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isOpen) {
            setScope(initialScope || null);
            setQuery('');
            setResults([]);
            setTimeout(() => inputRef.current?.focus(), 100);

            // Load decrypted cache for E2EE search - works for both web and desktop
            if (browserDecryptedCache.length === 0) {
                api.getEncryptedSearchCache()
                    .then(rawCache => {
                        if (rawCache.length > 0 && _decryptMessages) {
                            const decrypted = _decryptMessages(rawCache);
                            setBrowserDecryptedCache(decrypted);
                        }
                    })
                    .catch(console.error);
            }
        }
    }, [isOpen, initialScope]); // eslint-disable-line react-hooks/exhaustive-deps

    // Enrich a raw local DB message row with author and source objects from AppContext
    const enrichLocalMessage = (msg: any, isBrowserFallback = false): any => {
        const author = users.find(u => u.id === msg.author_id) || null;

        let source: any = null;
        let sourceType: string | null = null;

        if (msg.channel_id) {
            source = channels.find(c => c.id === msg.channel_id) || { id: msg.channel_id, name: 'Unknown Channel' };
            sourceType = 'channel';
        } else if (msg.dm_id) {
            const dm = directMessages.find(d => d.id === msg.dm_id);
            source = dm
                ? { id: dm.id, name: 'Direct Message', participants: (dm as any).participants }
                : { id: msg.dm_id, name: 'Direct Message' };
            sourceType = 'dm';
        } else if (msg.topic_id) {
            source = topics.find(t => t.id === msg.topic_id) || { id: msg.topic_id, name: 'Unknown Group' };
            sourceType = 'topic';
        } else if (msg.ticket_id) {
            source = tickets.find(t => t.id === msg.ticket_id) || { id: msg.ticket_id, title: 'Unknown Ticket' };
            sourceType = 'ticket';
        }

        return { ...msg, author, source, sourceType, _fromLocal: true, _isBrowserFallback: isBrowserFallback };
    };

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }

        setIsLoading(true);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);

        searchTimeout.current = setTimeout(async () => {
            try {
                const localFilter: Record<string, string> = scope ? { [`${scope.type}_id`]: scope.id } : {};

                let localEnriched: any[] = [];
                let serverResults: any[] = [];

                // 1. Run server search
                serverResults = await api.searchMessages(query, scope?.id, scope?.type).catch(() => [] as any[]);

                // 2. Fetch local DB + decrypted encrypted cache
                const lq = query.toLowerCase();

                if (isElectron) {
                    // Search local SQLite (plaintext messages)
                    const localRaw = await (window as any).electronAPI.searchMessages(query, localFilter, 100).catch(() => [] as any[]);
                    // Only include results where source is known (channel/DM/topic/ticket in current state)
                    const localFiltered = localRaw.filter((m: any) => {
                        if (m.channel_id) return channels.some((c: any) => c.id === m.channel_id);
                        if (m.dm_id) return directMessages.some((d: any) => d.id === m.dm_id);
                        if (m.topic_id) return topics.some((t: any) => t.id === m.topic_id);
                        if (m.ticket_id) return tickets.some((t: any) => t.id === m.ticket_id);
                        return false;
                    });
                    localEnriched = localFiltered.map((m: any) => enrichLocalMessage(m, false));

                    // Also search decrypted cache (covers E2EE messages)
                    if (browserDecryptedCache.length > 0) {
                        const matchedCache = browserDecryptedCache.filter((m: any) => {
                            let matchesScope = true;
                            if (scope) {
                                if (scope.type === 'channel' && m.channel_id !== scope.id) matchesScope = false;
                                if (scope.type === 'dm' && m.dm_id !== scope.id) matchesScope = false;
                                if (scope.type === 'topic' && m.topic_id !== scope.id) matchesScope = false;
                                if (scope.type === 'ticket' && m.ticket_id !== scope.id) matchesScope = false;
                            }
                            return matchesScope && m.content && m.content.toLowerCase().includes(lq);
                        });
                        const cacheEnriched = matchedCache.map((m: any) => enrichLocalMessage(m, true));
                        // Merge: avoid duplicates with localEnriched
                        const localIds = new Set(localEnriched.map((m: any) => m.id));
                        localEnriched = [...localEnriched, ...cacheEnriched.filter((m: any) => !localIds.has(m.id))];
                    }
                } else if (browserDecryptedCache.length > 0) {
                    const matchedBrowser = browserDecryptedCache.filter((m: any) => {
                        let matchesScope = true;
                        if (scope) {
                            if (scope.type === 'channel' && m.channel_id !== scope.id) matchesScope = false;
                            if (scope.type === 'dm' && m.dm_id !== scope.id) matchesScope = false;
                            if (scope.type === 'topic' && m.topic_id !== scope.id) matchesScope = false;
                            if (scope.type === 'ticket' && m.ticket_id !== scope.id) matchesScope = false;
                        }
                        return matchesScope && m.content && m.content.toLowerCase().includes(lq);
                    });
                    localEnriched = matchedBrowser.map((m: any) => enrichLocalMessage(m, true));
                }

                // Merge: server results take precedence (already enriched by server);
                // local fills in what server skipped due to E2EE
                const serverIds = new Set((serverResults as any[]).map((m: any) => m.id));
                const localOnly = localEnriched.filter(m => !serverIds.has(m.id));

                const merged = [...(serverResults as any[]), ...localOnly]
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                setResults(merged);
            } catch (err) {
                console.error('Search error', err);
            } finally {
                setIsLoading(false);
            }
        }, 300);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, scope, browserDecryptedCache, users, channels, directMessages, topics, tickets]);

    if (!isOpen) return null;

    const getIconForSource = (type: string, isPrivate?: boolean) => {
        const props = { size: 14, style: { marginRight: 4 } };
        switch (type) {
            case 'channel': return isPrivate ? <Lock {...props} /> : <Hash {...props} />;
            case 'dm': return <Users {...props} />;
            case 'ticket': return <ClipboardList {...props} />;
            case 'topic': return <Layers {...props} />;
            default: return <Hash {...props} />;
        }
    };

    const getSourceName = (msg: any): string => {
        if (msg.sourceType === 'channel') return msg.source?.name || 'Channel';
        if (msg.sourceType === 'ticket') return msg.source?.title || 'Ticket';
        if (msg.sourceType === 'dm') {
            const others = (msg.source?.participants || []).filter((p: any) => p.id !== msg.author_id);
            return others.length > 0 ? others.map((p: any) => p.name).join(', ') : 'Direct Message';
        }
        return msg.source?.name || 'Unknown';
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1000, alignItems: 'flex-start', paddingTop: '8vh', background: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: 720, maxWidth: '95vw', padding: 0, overflow: 'hidden', background: 'var(--bg-primary)', borderRadius: 'var(--radius-lg)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>

                {/* Search Input */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Search size={20} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />

                    {scope && (
                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(99,102,241,0.12)', padding: '3px 8px', borderRadius: 6, fontSize: 13, color: 'var(--accent-primary)', flexShrink: 0 }}>
                            <span style={{ marginRight: 4, opacity: 0.7 }}>in:</span>
                            {getIconForSource(scope.type)}
                            <span style={{ fontWeight: 600 }}>{scope.name}</span>
                            <button className="btn btn-ghost btn-icon btn-xs" onClick={() => setScope(null)} style={{ marginLeft: 4, color: 'var(--accent-primary)' }}>
                                <X size={12} />
                            </button>
                        </div>
                    )}

                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={scope ? 'Search in this conversation...' : 'Search messages, channels, DMs, tickets...'}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Escape' && onClose()}
                        style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 17, outline: 'none', color: 'var(--text-primary)', minWidth: 0 }}
                    />

                    {!scope && initialScope && (
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setScope(initialScope)}
                            style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                        >
                            Search in {initialScope.name} instead
                        </button>
                    )}

                    {scope && (
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setScope(null)}
                            style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}
                        >
                            Search everywhere
                        </button>
                    )}

                    <button className="btn btn-ghost btn-icon" onClick={onClose} style={{ color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Results */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>

                    {!query.trim() && (
                        <div style={{ padding: '40px 32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                            <Search size={36} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.3 }} />
                            <p style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Search across your workspace</p>
                            <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                                Searches all messages, including end-to-end encrypted ones safely decrypted locally.
                            </p>
                        </div>
                    )}

                    {query.trim() && isLoading && results.length === 0 && (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Searching...</div>
                    )}

                    {query.trim() && !isLoading && results.length === 0 && (
                        <div style={{ padding: '40px 32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                            <p style={{ margin: 0, fontWeight: 500 }}>No results for "{query}"</p>
                        </div>
                    )}

                    {results.map(msg => {
                        const sourceName = getSourceName(msg);
                        const authorId = msg.author?.id || msg.author_id || '';

                        return (
                            <div
                                key={msg.id}
                                style={{ padding: '11px 18px', borderBottom: '1px solid var(--border-color)', cursor: 'pointer', display: 'flex', gap: 12, transition: 'background 0.1s' }}
                                onClick={() => {
                                    if (msg.sourceType && msg.source?.id) {
                                        // Store target message ID so MessageList can scroll to it
                                        sessionStorage.setItem('scrollToMessageId', msg.id);
                                        setActiveView({ type: msg.sourceType, id: msg.source.id });
                                        onClose();
                                    }
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                                <div className="avatar avatar-sm" style={{ background: getAvatarColor(authorId), flexShrink: 0 }}>
                                    {(msg.author?.name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                                            <span style={{ fontWeight: 600, fontSize: 14, flexShrink: 0 }}>{msg.author?.name || 'Unknown'}</span>
                                            <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
                                                {new Date(msg.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', padding: '2px 7px', borderRadius: 4, flexShrink: 0, maxWidth: 180 }}>
                                            {getIconForSource(msg.sourceType, msg.source?.is_private)}
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {sourceName}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 14, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {msg.content}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                {results.length > 0 && (
                    <div style={{ padding: '8px 18px', borderTop: '1px solid var(--border-color)', fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
